import { baseURL } from "database/onlineDb/baseUrl";

export const ENTITLEMENT_API_VERSION = "telemed.entitlement.v1";
export const ENTITLEMENT_EVENT_TYPE = "account.entitlement.changed";
const PROJECTION_ERRORS = {
  AUTHENTICATION_REQUIRED: {
    status: 401,
    message: "Authentication is required.",
    retryable: false,
  },
  FORBIDDEN: {
    status: 403,
    message: "You are not permitted to access entitlement data.",
    retryable: false,
  },
  INVALID_REQUEST: {
    status: 400,
    message: "The request is invalid.",
    retryable: false,
  },
  RATE_LIMITED: {
    status: 429,
    message: "Too many entitlement requests. Try again later.",
    retryable: true,
  },
  ENTITLEMENT_UNAVAILABLE: {
    status: 500,
    message: "Entitlement request is temporarily unavailable. Try again later.",
    retryable: true,
  },
  INTERNAL_ERROR: {
    status: 500,
    message: "Entitlement request could not be completed. Try again later.",
    retryable: true,
  },
};

const UINT64_MAX = "18446744073709551615";
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const exact = (value, fields) =>
  record(value) &&
  Object.keys(value).length === fields.length &&
  fields.every((field) => own(value, field));
const timestamp = (value) =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value) &&
  !Number.isNaN(Date.parse(value)) &&
  new Date(value).toISOString() === value;
const opaque = (value) =>
  typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value);
const projectionVersion = (value) =>
  typeof value === "string" &&
  /^(0|[1-9][0-9]*)$/.test(value) &&
  (value.length < UINT64_MAX.length ||
    (value.length === UINT64_MAX.length && value <= UINT64_MAX));
const canonicalMoney = (value) => {
  if (typeof value !== "string" || !/^(0|[1-9][0-9]*)\.[0-9]{2}$/.test(value)) {
    return false;
  }
  const [whole] = value.split(".");
  return whole.length < 10 || (whole.length === 10 && whole <= "9999999999");
};
const cents = (value) => {
  const [whole, fraction] = value.split(".");
  return Number(whole) * 100 + Number(fraction);
};
const unitCount = (value) =>
  Number.isSafeInteger(value) && value >= 0 && value <= 4294967295;

export const validateEntitlementChangedEvent = (value) => {
  if (
    !exact(value, [
      "protocol",
      "eventId",
      "eventType",
      "version",
      "occurredAt",
    ]) ||
    value.protocol !== ENTITLEMENT_API_VERSION ||
    !opaque(value.eventId) ||
    value.eventType !== ENTITLEMENT_EVENT_TYPE ||
    value.version !== 1 ||
    !timestamp(value.occurredAt)
  ) {
    throw new Error("invalid entitlement event");
  }
  return { ...value };
};

export const validateEntitlementProjection = (value) => {
  const valid =
    exact(value, ["projectionVersion", "asOf", "scope", "paid", "proBono"]) &&
    projectionVersion(value.projectionVersion) &&
    timestamp(value.asOf) &&
    value.scope === "authenticated-organization" &&
    exact(value.paid, ["total", "reserved", "available"]) &&
    canonicalMoney(value.paid.total) &&
    canonicalMoney(value.paid.reserved) &&
    canonicalMoney(value.paid.available) &&
    cents(value.paid.total) ===
      cents(value.paid.reserved) + cents(value.paid.available) &&
    exact(value.proBono, ["totalUnits", "reservedUnits", "availableUnits"]) &&
    unitCount(value.proBono.totalUnits) &&
    unitCount(value.proBono.reservedUnits) &&
    unitCount(value.proBono.availableUnits) &&
    value.proBono.totalUnits ===
      value.proBono.reservedUnits + value.proBono.availableUnits;
  if (!valid) throw new Error("invalid entitlement projection");
  return {
    ...value,
    paid: { ...value.paid },
    proBono: { ...value.proBono },
  };
};

export class EntitlementProjectionError extends Error {
  constructor({ message, code, requestId, retryable, status } = {}) {
    const fallback = PROJECTION_ERRORS.ENTITLEMENT_UNAVAILABLE;
    super(message || fallback.message);
    this.name = "EntitlementProjectionError";
    this.code = code || "ENTITLEMENT_UNAVAILABLE";
    this.requestId = requestId || null;
    this.retryable =
      retryable === undefined ? fallback.retryable : Boolean(retryable);
    this.status = status || 0;
  }
}

const validateError = (payload, status) => {
  const expected = payload?.error?.code
    ? PROJECTION_ERRORS[payload.error.code]
    : null;
  if (
    !exact(payload, ["apiVersion", "error"]) ||
    payload.apiVersion !== ENTITLEMENT_API_VERSION ||
    !exact(payload.error, ["code", "message", "requestId", "retryable"]) ||
    !expected ||
    expected.status !== status ||
    payload.error.message !== expected.message ||
    typeof payload.error.requestId !== "string" ||
    !/^[\x20-\x7e]{1,128}$/.test(payload.error.requestId) ||
    payload.error.retryable !== expected.retryable
  ) {
    throw new EntitlementProjectionError({ status });
  }
  throw new EntitlementProjectionError({ ...payload.error, status });
};

export const fetchEntitlementProjection = async ({
  token,
  baseUrl = baseURL,
  fetchImpl,
  signal,
} = {}) => {
  if (typeof token !== "string" || token.length === 0) {
    throw new EntitlementProjectionError({
      code: "AUTHENTICATION_REQUIRED",
      message: "Authentication is required.",
      retryable: false,
      status: 401,
    });
  }
  const request =
    fetchImpl ||
    (typeof window !== "undefined" ? window.fetch.bind(window) : null);
  if (!request) throw new EntitlementProjectionError();

  let response;
  let payload;
  try {
    response = await request(`${baseUrl}/api/v1/entitlement/projection`, {
      method: "GET",
      cache: "no-store",
      ...(signal ? { signal } : {}),
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    payload = await response.json();
  } catch (error) {
    if (error instanceof EntitlementProjectionError) throw error;
    throw new EntitlementProjectionError();
  }

  if (!response.ok) return validateError(payload, response.status);
  if (
    !exact(payload, ["apiVersion", "data"]) ||
    payload.apiVersion !== ENTITLEMENT_API_VERSION
  ) {
    throw new EntitlementProjectionError({ status: response.status });
  }
  try {
    return validateEntitlementProjection(payload.data);
  } catch (error) {
    throw new EntitlementProjectionError({ status: response.status });
  }
};
